import Vue, { VNode } from "vue";
import App from "./App.vue";
import Loader from "@/components/Common/Loader/Loader.vue";
import "./registerServiceWorker";
import router from "./router";
import store, { ACTIONS, StateCurrency } from "./store";
import VueApollo from "vue-apollo";
import VueI18n from "vue-i18n";
import { apolloClient } from "./apollo-client";
import VueLazyload from "vue-lazyload";
import { DirectiveBinding } from "vue/types/options";
import AirbnbStyleDatepicker from "vue-airbnb-style-datepicker";
import VueMask from "v-mask";
import "vue-airbnb-style-datepicker/dist/vue-airbnb-style-datepicker.min.css";
import { defaultOptions } from "./helpers/datepickerOptions";
import * as VueGoogleMaps from "vue2-google-maps";
//import VueHead from "vue-head";
import gql from "graphql-tag";
import VueMeta from "vue-meta";


// Declarar la interfaz para fbq
declare global {
  interface Window {
    fbq: {
      (command: 'track', event: string, data?: any): void;
      (command: 'trackCustom', event: string, data?: object): void;
      (command: 'init', pixelId: string): void;
      (command: 'track', event: 'PageView'): void;
      q?: any[];
    };
  }
}

window.fbq = window.fbq || ((...args: any[]) => {
  (window.fbq.q = window.fbq.q || []).push(args);
}) as Window['fbq'];


const apolloProvider = new VueApollo({
  defaultClient: apolloClient
});

Vue.config.productionTip = false;
Vue.use(VueI18n);
Vue.use(VueApollo);
Vue.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyDiE8vrtlsS6RELmzH_enMDuHt6KOfTNiE",
    libraries: "places"
  }
});
Vue.use(VueLazyload);
Vue.use(AirbnbStyleDatepicker, defaultOptions());
Vue.use(VueMask);

// Primitiva compartida: global para que cualquier pantalla la use sin
// registrarla, y eager porque cargar un spinner async no tiene sentido.
Vue.component("Loader", Loader);

Vue.use(VueMeta, {
  refreshOnceOnNavigation: true
})

const i18n = new VueI18n({
  locale: "es",
  fallbackLocale: "es",
  silentTranslationWarn: true
});

interface MyHTMLElement extends HTMLElement {
  clickOutsideEvent: EventListener;
}

interface VueCustomSelect extends Vue {
  open: boolean;
}

Vue.directive("click-outside", {
  bind: (el: HTMLElement, binding: DirectiveBinding, vNode: VNode) => {
    (el as MyHTMLElement).clickOutsideEvent = function(event: Event) {
      if (!(el == event.target || el.contains(event.target as Node))) {
        const elem = vNode?.context?.$refs[binding.value] as VueCustomSelect;
        elem.open = false;
      }
    };
    document.body.addEventListener(
      "click",
      (el as MyHTMLElement).clickOutsideEvent
    );
  },
  unbind: (el: HTMLElement) => {
    document.body.removeEventListener(
      "click",
      (el as MyHTMLElement).clickOutsideEvent
    );
  }
});

Vue.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyDiE8vrtlsS6RELmzH_enMDuHt6KOfTNiE"
  }
});

(window as any).app = new Vue({
  i18n,
  router,
  store,
  apolloProvider,
  render: h => h(App as any),
  async beforeMount() {
    await this.loadCountry();
  },
  data: {
    window: {
      width: 0,
      height: 0
    },
    ready: false,
    partnerSlug: ''
  },
  async created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    await this.validateToken();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    ajaxCall(url: string): Promise<string> {
      return new Promise((accept, reject) => {
        try {
          const xhttp = new XMLHttpRequest();
          xhttp.onreadystatechange = function() {
            if (this.readyState == 4 && this.status == 200) {
              accept(this.responseText);
            } else if (this.readyState == 4) {
              reject(this.responseText);
            }
          };
          xhttp.open("GET", url, true);
          xhttp.send();
        } catch (e) {
          reject(JSON.stringify(e));
        }
      });
    },
    async loadCountry() {
      let lang = "es";
      let code = "AR";
      try {
        const response = await this.ajaxCall(
          `${process.env.VUE_APP_API_URL}ipinfo`
        );
        let ipdata = JSON.parse(response);
        if (!ipdata.country) {
          try {
            const ipresponse = await this.ajaxCall("https://ipinfo.io/json");
            if (ipresponse) ipdata = JSON.parse(ipresponse); //.data;
          } catch (e) {
            console.log(e);
          }
        }
        code = ipdata.country || "AR";
        const posresponse = await this.ajaxCall(
          `${process.env.VUE_APP_API_URL}pos/${code}`
        );
        const pos = JSON.parse(posresponse);
        this.$store.dispatch(ACTIONS.SET_POS, pos);
        lang = pos.language;
        await this.loadLanguage(lang);
      } catch (e) {
        console.log(e);
      }
    },
    async loadLanguage(lang: string) {
      try {
        const response: string = await this.ajaxCall(`${process.env.VUE_APP_API_URL}t/${lang}`);
        i18n.setLocaleMessage(lang, JSON.parse(response));
        i18n.locale = lang;
        (window as any).app.ready = true;
      } catch (e) {
        console.log(e);
      }
    },
    getPrice(
      price: number | undefined,
      currency: string | undefined,
      userMarkup: number | undefined
    ) {
      if (!price) return 0;
      const markup = userMarkup ? 1 + userMarkup / 100 : 1;
      const currentCurrency = this.$store.state
        .currentCurrency as StateCurrency;
      if (currency === currentCurrency.code) return price * markup;
      if (currency !== "USD") {
        const rateCurrency = (this.$store.state.currencies as Array<
          StateCurrency
        >).find((c: StateCurrency) => c.code === currency);
        price = price / (rateCurrency?.usdPrice ?? 1);
      }
      return (
        price *
        currentCurrency.usdPrice *
        (currentCurrency.markup ? 1 + currentCurrency.markup / 100 : 1) *
        markup
      );
    },
    formatMoney(value: number, toFixed = 2) {
      // Sin decimales
      return Number(value)
        .toFixed(0)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
    convert(price: number, fromCurrency: string, toCurrency: string) {
      if (fromCurrency === toCurrency) return price;
      const currencyA: StateCurrency | undefined = (this.$store.state
        .currencies as Array<StateCurrency>).find(
        (c: StateCurrency) => c.code === fromCurrency
      );
      const currencyB: StateCurrency | undefined = (this.$store.state
        .currencies as Array<StateCurrency>).find(
        (c: StateCurrency) => c.code === toCurrency
      );

      if (currencyA && currencyB) {
        return (
          (price / (currencyA.usdPrice ?? 1)) *
          (currencyB.usdPrice ?? 1) *
          (currencyB.markup ? 1 + currencyB.markup / 100 : 1)
        );
      }
      return price;
    },
    decodeHTMLEntities(text: any) {
      const textArea = document.createElement("textarea");
      textArea.innerHTML = text;
      return textArea.value;
    },
    async validateToken() {      
      console.log("!!!!!!!!!!!!!!!!!!! ValidateToken");
      if (!this.$store.state.user.id) return;
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation {
              validateToken {
                authToken
              }
            }
          `,
          variables: {},
          optimisticResponse: {
            __typename: "Mutation",
            validateToken: {
              __typename: "ValidateToken",
              authToken: this.$store.state.user.token
            }
          }
        });
        if (response.data?.validateToken.authToken) {
          this.$store.dispatch(ACTIONS.SET_LOGIN, {
            ...this.$store.state.user,
            token: response.data?.validateToken.authToken,
            isLogged: true
          });
        } else {
          // Hay que leerlo antes de limpiar la sesion. Una agencia que entro
          // desde el sitio de un socio no tiene contrasena, asi que mandarla al
          // formulario de login la deja sin salida.
          const viaSso = this.$store.state.user.viaSso;
          this.$store.dispatch(ACTIONS.SET_LOGIN, null);
          if (this.$router.currentRoute.path !== "/") {
            // window.location.href = "/";
            window.location.href = viaSso
              ? `${process.env.VUE_APP_BACKEND_URL}session-ended`
              : `${process.env.VUE_APP_BACKEND_URL}login/agencies`;
          }
        }
      } catch (e) {
        this.$store.dispatch(ACTIONS.SET_LOGIN, null);
        if (this.$router.currentRoute.path !== "/") {
          window.location.href = "/";
        }
      }
    }
  }
}).$mount("#app");
